import React, { Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { RouteConstants } from "./constants";
import { useRecoilValue } from "recoil";
import { authState } from './states';
import { Spinner } from "./ui-elements";

interface ProtectedRouteProps {
    auth: any;
    redirectPath: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ auth, redirectPath }) => {
    if (!auth || !Object.keys(auth).length) {
        return <Navigate to={redirectPath} />;
    }
    return <Outlet />;
}

const Router = () => {
    const auth = useRecoilValue(authState);
    const Login = React.lazy(() => import("./pages/Login"));
    const UpdatePassword = React.lazy(() => import("./pages/UpdatePassword"));
    const AppLayout = React.lazy(() => import('./pages/AppLayout'));
    const HomeLayout = React.lazy(() => import('./pages/HomeLayout'));
    const Home = React.lazy(() => import("./pages/Home"));
    const Catchments = React.lazy(() => import("./pages/Catchments"));
    const SidebarLayout = React.lazy(() => import('./pages/SidebarLayout'));
    const Dashboard = React.lazy(() => import("./pages/Dashboard"));
    const NoPoi = React.lazy(() => import("./pages/NoPoi"));
    const Stories = React.lazy(() => import("./pages/Stories"));
    const Story = React.lazy(() => import("./pages/Story"));
    const AccountLayout = React.lazy(() => import('./pages/AccountLayout'));
    const Profile = React.lazy(() => import("./pages/Profile"));
    const Settings = React.lazy(() => import("./pages/Settings"));
    const Users = React.lazy(() => import("./pages/Users"));
    const SomethingWentWrong = React.lazy(() => import("./pages/SomethingWentWrong"));
    const NotFound = React.lazy(() => import("./pages/NotFound"));
    const Help = React.lazy(() => import("./pages/Help"));
    const Compare = React.lazy(() => import("./pages/Compare"));
    const Wip = React.lazy(() => import("./pages/Wip"));
    const Releases = React.lazy(() => import("./pages/Releases"));
    const Layers = React.lazy(() => import("./pages/Layers"));
    const Components = React.lazy(() => import("./pages/Components"));

    return (
        <Suspense fallback={
            <Spinner spinnerFlag={true} />
        }>
            <Routes>
                <Route path={RouteConstants.login} element={auth && Object.keys(auth).length ? <Navigate to={RouteConstants.root} /> : <Login />} />
                <Route path={RouteConstants.update_password} element={<UpdatePassword />} />
                <Route element={<ProtectedRoute auth={auth} redirectPath={RouteConstants.login} />}>
                    <Route path={RouteConstants.something_went_wrong} element={<SomethingWentWrong />} />
                    <Route path={RouteConstants.root} element={<AppLayout />}>
                        <Route element={<HomeLayout />}>
                            <Route index element={<Home />} />
                            <Route path={RouteConstants.catchments} element={<Catchments />} />
                        </Route>
                        <Route path={RouteConstants.no_poi} element={<NoPoi />} />
                        <Route element={<SidebarLayout />}>
                            <Route path={RouteConstants.dashboard} element={<Dashboard />} />
                            <Route path={RouteConstants.wip} element={<Wip />} />
                            <Route path={RouteConstants.stories} element={<Stories />} />
                            <Route path={RouteConstants.story_details} element={<Story />} />
                        </Route>
                        <Route element={<AccountLayout />}>
                            <Route path={RouteConstants.profile} element={<Profile />} />
                            <Route path={RouteConstants.settings} element={<Settings />} />
                            <Route path={RouteConstants.users} element={<Users />} />
                        </Route>
                        <Route path={RouteConstants.not_found} element={<NotFound />} />
                        <Route path={RouteConstants.help} element={<Help />} />
                        <Route path={RouteConstants.compare} element={<Compare/>} />
                        <Route path={RouteConstants.releases} element={<Releases />} />
                        <Route path={RouteConstants.layers} element={<Layers />} />
                        <Route path={RouteConstants.components} element={<Components />} />
                    </Route>
                </Route>
            </Routes>
        </Suspense >
    );
};

export default Router;
